import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useLeadListMutations from "../../datahooks/useLeadListMutations";
import { CreateListSchema, type CreateListSchemaType } from "../../schemas";

import type { LeadList } from "../../types";

interface LeadListRenameProps {
  leadList: LeadList;
  onBack: () => void;
  onClose: () => void;
}

export default function LeadListRename({
  leadList,
  onBack,
  onClose,
}: LeadListRenameProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { renameList, isRenamingList } = useLeadListMutations();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateListSchemaType>({
    defaultValues: { name: leadList.name },
    resolver: valibotResolver(CreateListSchema),
  });

  function onRename(data: CreateListSchemaType) {
    renameList({ listId: leadList.id, name: data.name }).then(() => {
      onClose();
    });
  }

  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  return (
    <form onSubmit={handleSubmit(onRename)}>
      <Header title="Rename list" />

      <Body>
        <FormField
          label="List name"
          htmlFor="listName"
          error={errors.name?.message}
        >
          <MaxLengthInput
            id="listName"
            placeholder="Enter a list name here"
            characterLength={watch("name")?.length}
            maxLength={getSchemaMaxLength(CreateListSchema.entries.name)}
            autoFocus
            {...register("name")}
          />
        </FormField>
      </Body>

      <Footer className="justify-end">
        <Button
          type="button"
          variant="secondary-black"
          disabled={isRenamingList}
          onClick={onBack}
        >
          Cancel
        </Button>

        <Button type="submit" isLoading={isRenamingList}>
          Save
        </Button>
      </Footer>
    </form>
  );
}
